import { useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { desksEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { DESK_CREATE } from '@/components/organisms/Modals/ModalConductor';
import { useAccount } from '@/hooks/useAccount';
import { useIntegrations } from '@/hooks/useIntegrations';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDeskId } from '@/redux/session/actions';
import { selectAccountId } from '@/redux/session/selectors';
import { Desk, PartialDesk } from '@/shared/types/desk';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isLimitReached } from '@/util/limits';
import { preventClickThrough } from '@/util/util';

export const API = Object.freeze({
  listDesks: async (): Promise<Desks> => callGet(endpoints.desks),

  getDesk: async (deskId: string): Promise<Desk> => callGet(endpoints.desk(deskId)),

  createDesk: async (newDesk: Partial<Desk>): Promise<Desk> => callPost(endpoints.desks, newDesk),

  updateDesk: async ({ desk_id, ...desk }: PartialDesk): Promise<Desk> =>
    callPut(endpoints.desk(desk_id), desk),

  deleteDesk: async (deskId: string): Promise<Desk> => callDelete(endpoints.desk(deskId)),
});

export type Desks = { desks: Desk[] };

export const deskCache = createEntityCache<Desk, 'desks'>({
  listKey: () => [endpoints.desks],
  detailKey: (_parentId, deskId) => [endpoints.desk(deskId)],
  listField: 'desks',
  getId: (desk) => desk.desk_id,
});

const useDesks = (deskId?: string) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const { getIntegration } = useIntegrations(undefined);

  const { slug, account } = useAccount();

  const canRead = useSelector((state: RootState) => getPermissions(state, 'desks', actions.READ));

  const {
    data: desks,
    status: listStatus,
    isLoading: isLoadingDesks,
  } = useQuery<Desks, Error>({
    queryKey: [endpoints.desks],
    queryFn: () => API.listDesks(),
    enabled: !!accountId && canRead,
  });

  const { data: desk, status: getStatus } = useQuery<Desk, Error>({
    queryKey: [endpoints.desk(deskId)],
    queryFn: () => API.getDesk(deskId),
    enabled: !!deskId && canRead,
  });

  const { mutate: updateDesk, status: updateStatus } = useMutation<Desk, Error, PartialDesk>({
    mutationFn: API.updateDesk,
    onSuccess: (resp) => {
      deskCache.updated(queryClient, resp);
      toast.success(t('environments.environment_updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: createDesk,
    mutateAsync: createDeskAsync,
    status: createStatus,
  } = useMutation<Desk, Error, Partial<Desk>>({
    mutationFn: API.createDesk,
    onSuccess: (resp) => {
      deskCache.created(queryClient, { ...resp, account_id: accountId });

      toast.success(t('environments.environment_created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteDesk, status: deleteStatus } = useMutation<Desk, Error, string>({
    mutationFn: (id) => API.deleteDesk(id),
    onSuccess: (resp) => {
      dispatch(popModal());

      deskCache.removed(queryClient, { id: resp.desk_id });
      dispatch(setDeskId(null));
      const firstDeskIdId = desks?.desks?.filter((desk) => desk.desk_id !== resp.desk_id)?.[0]
        ?.desk_id;
      if (firstDeskIdId) {
        navigate(`/${slug}/environments/${firstDeskIdId}`);
      } else {
        navigate(`/${slug}/environments`);
      }
      toast.success(t('environments.environment_deleted'));
    },
    meta: { errorToast: true },
  });

  const handleCreateDesk = useCallback(
    (e) => {
      preventClickThrough(e);

      dispatch(pushModal(DESK_CREATE));
    },
    [dispatch]
  );

  const serviceDesks = useMemo(
    () => desks?.desks.filter((desk) => desk.is_service_desk),
    [desks?.desks]
  );

  const getAllIntegrations = useCallback(
    async (type: IntegrationType) => {
      if (desks?.desks) {
        const promises = desks?.desks?.reduce((acc, desk) => {
          const integrationsPerDesk = desk.integrations
            .filter((i) => i.type === type)
            .map((int) => getIntegration(desk.desk_id, int.integration_id));

          return [...acc, ...integrationsPerDesk];
        }, [] as Promise<Integration>[]);

        // Use allSettled so one failed integration doesn't break all
        const results = await Promise.allSettled(promises);
        const integrationsAllDesks = results
          .filter(
            (result): result is PromiseFulfilledResult<Integration> => result.status === 'fulfilled'
          )
          .map((result) => result.value);

        return integrationsAllDesks;
      }
      return [];
    },
    [desks, getIntegration]
  );

  const maxDesksReached = useMemo(() => {
    return isLimitReached(desks?.desks?.length ?? 0, account?.max_desks);
  }, [desks?.desks?.length, account?.max_desks]);

  const maxDesks = useMemo(() => account?.max_desks, [account?.max_desks]);
  return {
    desks: desks?.desks,
    maxDesks,
    maxDesksReached,
    listStatus,
    isLoadingDesks,
    getStatus,
    serviceDesks,
    handleCreateDesk,
    desk,
    createStatus,
    deleteStatus,
    updateStatus,
    createDesk,
    createDeskAsync,
    deleteDesk,
    updateDesk,
    getAllIntegrations,
  };
};

export default useDesks;
